import { Session } from 'next-auth';

import { JourneyType as JourneyTypeEnum } from '@bytel/leviator';

import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';
import { personServices } from '@services/person';

import { SapicMapper } from '@mappers/sapic';

import {
    JourneyType,
    PromotionDetail,
    SapicCategoryId,
    SapicContextualisedProductPayload,
    SapicCustomerCategory,
    SapicFundingMode,
    SapicPriceForFilter,
    SapicPriceForSort,
    SapicProductPayload,
    SapicProductType,
    SapicResponseType,
} from '@app-types/api/sapic';
import { DiscountEnumType } from '@app-types/discount';
import { FaiType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';
import { UserType } from '@app-types/user';

import { SAPIC_X_VERSION } from '@constants/api';
import { PlanTypeId } from '@constants/plan';
import { CATEGORY_TYPE, PLAY } from '@constants/provider';

import { z } from 'zod';

export type SapicGetOptionsType = {
    category?: SapicCategoryId;
    categories?: (SapicCategoryId | PlanTypeId)[];
    sort?: string;
    offset?: number;
    limit?: number;
    fundingMode?: SapicFundingMode;
    priceFilter?: SapicPriceForFilter;
    priceSort?: SapicPriceForSort;
    plan?: string;
    contract?: string;
    gencodes?: string[];
    withDetails?: boolean;
    filterByTypes?: SapicCategoryId[];
    filterByGencodes?: string[];
    excludeCategoryType?: CATEGORY_TYPE;
    play?: PLAY;
    searchParams?: Record<string, string>;
    inStock?: true;
};

const getProductPayloadApiSchema = z.object({
    referenceExterneParcoursCible: z.string(),
    panier: z.object({
        parcours: z.array(
            z.object({
                referenceExterne: z.string(),
                typeParcours: z.union([
                    z.literal(JourneyTypeEnum.MOBILE_ACQUISITION),
                    z.literal(JourneyTypeEnum.FAI_ACQUISITION),
                    z.literal(JourneyTypeEnum.RENEWAL),
                ]),
                contrat: z
                    .object({
                        identifiant: z.string(),
                    })
                    .optional(),
                statut: z.union([z.literal('EN_COURS'), z.literal('TERMINE')]),
                elements: z.array(
                    z.object({
                        referenceExterne: z.string(),
                        type: z.enum(['ELEMENT_PANIER', 'LIVRAISON']),
                        composantCommercial: z.object({ identifiant: z.string(), typeCatalogue: z.literal('BYTEL') }),
                    }),
                ),
            }),
        ),
        acheteur: z.object({
            type: z.union([z.literal('PERSONNE'), z.literal('NON_CONNU_GP')]),
            noPersonne: z.string().optional(),
        }),
    }),
});

const appConfig = getAppConfig();

class SapicRepository {
    private readonly url: string;

    private defaultGetProductsOptions: SapicGetOptionsType = {
        sort: 'nouveautes',
        offset: 0,
        limit: 12,
        fundingMode: SapicFundingMode.AUTO,
        priceFilter: SapicPriceForFilter.INITIAL,
        priceSort: SapicPriceForSort.INITIAL,
        withDetails: false,
    };

    public constructor(url: string) {
        this.url = url;
    }

    public async getFaiPlans(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<{ products: FaiType[]; count: number }> {
        return this.contextualisedProducts<SapicResponseType>(virtualCart, options, session).then((response) => ({
            products: response.produits.map((product) => SapicMapper.mapToFai(product)),
            count: response.nombreTotalProduits,
        }));
    }

    private extractPromoLinks(products: SapicProductType[]): string[] {
        return (
            products
                ?.flatMap(
                    (product) =>
                        product.simulation?.panier.parcours.flatMap((journey) =>
                            journey.elements.flatMap(
                                (el) =>
                                    el.promotionsCalculees?.promotionsAppliquees?.map(
                                        (promo) => promo._links.consulterPromotion.href,
                                    ) ?? [],
                            ),
                        ) ?? [],
                )
                .filter(Boolean) ?? []
        );
    }

    private async fetchPromotionDetails(promoLinks: string[]): Promise<PromotionDetail[]> {
        if (!promoLinks.length) return [];

        try {
            const uniqueLinks = [...new Set(promoLinks)];
            return await Promise.all(uniqueLinks.map((link) => this.getPromotionDetails(link)));
        } catch (e) {
            console.error('Error fetching promotion details:', e);
            return [];
        }
    }

    private mapProductWithPromotions(product: MobilePlanType, allPromos: PromotionDetail[]): MobilePlanType {
        if (!product.simulatedCart) {
            return {
                ...product,
                simulatedCart: { fundings: [], quotes: [] },
            };
        }

        return {
            ...product,
            simulatedCart: {
                fundings: product.simulatedCart.fundings ?? [],
                quotes:
                    product.simulatedCart.quotes?.map((quote) => ({
                        ...quote,
                        products: quote.products?.map((simulatedProduct) => ({
                            ...simulatedProduct,
                            details: {
                                ...simulatedProduct.details,
                                discounts: simulatedProduct.details.discounts.map((discount) => {
                                    const promoDetails = allPromos.find((p) => p.identifiant === discount.label);
                                    const isOdr = promoDetails?.caracteristiques?.some((c) => c.valeur === 'ODR');
                                    return {
                                        ...discount,
                                        type: isOdr ? DiscountEnumType.ODR : discount.type,
                                    };
                                }),
                            },
                        })),
                    })) ?? [],
            },
        };
    }

    public async getMobilePlans(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<{ products: MobilePlanType[]; count: number }> {
        try {
            const response = await this.contextualisedProducts<SapicResponseType>(virtualCart, options, session);
            const promoLinks = this.extractPromoLinks(response.produits);
            const allPromos = await this.fetchPromotionDetails(promoLinks);

            const mappedProducts = response.produits
                .map((product) => SapicMapper.mapToMobilePlan(product))
                .map((product) => this.mapProductWithPromotions(product, allPromos));

            return {
                products: mappedProducts,
                count: response.nombreTotalProduits,
            };
        } catch (error) {
            console.error('Error in getMobilePlans:', error);
            return {
                products: [],
                count: 0,
            };
        }
    }

    public async getPromotionDetails(link: string): Promise<PromotionDetail> {
        return apiHttpService.get(`${this.url}/${link}`, undefined, {
            ttl: Number.parseInt(appConfig.cache.ttl.products),
            tags: [CACHE_TAGS.PRODUCTS],
        });
    }

    readonly getProductWallPromotionsLinks = (productList: SapicProductType[]) => {
        return productList.flatMap((product) =>
            product.promotions.flatMap((promo) => promo._links.consulterPromotion.href),
        );
    };

    public async getSubscribableOptions(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<{ products: Product[]; count: number }> {
        return this.contextualisedProducts<SapicResponseType>(virtualCart, options, session).then(async (response) => {
            const products = response.produits.map((product) => SapicMapper.mapToOption(product));
            return {
                products,
                count: response.nombreTotalProduits,
            };
        });
    }

    public async getProducts(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<{ products: Product[]; count: number }> {
        return this.contextualisedProducts<SapicResponseType>(virtualCart, options, session).then(async (response) => {
            const promoLinks = this.getProductWallPromotionsLinks(response.produits);
            const allPromos = await this.fetchPromotionDetails(promoLinks);
            const baseProducts = response.produits.map((product) => SapicMapper.mapToProduct(product) as Product);
            const productsWithOdr = baseProducts.map((product) => {
                const odrDiscounts = product.details.discounts
                    .filter((discount) => {
                        const promoDetails = allPromos.find((p) => p.identifiant === discount.label);
                        return promoDetails?.caracteristiques?.some((c) => c.valeur === 'ODR');
                    })
                    .map((discount) => discount.value ?? 0);

                const odrTotal = odrDiscounts.length > 0 ? odrDiscounts.reduce((a, b) => a + b) : 0;
                return {
                    ...product,
                    price: Math.max(1, (product.price ?? 0) - odrTotal),
                };
            });

            return {
                products: productsWithOdr,
                count: response.nombreTotalProduits,
            };
        });
    }

    private async contextualisedProducts<T>(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<T> {
        const headers: Record<string, string> = {
            'x-version': SAPIC_X_VERSION.V5,
        };

        const user = session?.user as UserType | undefined;

        let idPu: string | undefined;
        if (user) {
            idPu = await personServices.getIdPuFromContract(
                user,
                options.contract ?? virtualCart?.cart?.quotes[0]?.contractId,
            );
        }

        try {
            const body = getProductPayloadApiSchema.parse(this.createSapicPayload(virtualCart, idPu));
            const params = this.generateSearchParam(options, idPu);
            return await apiHttpService.post<T>(
                `${this.url}/catalogue/produits-contextualises?${new URLSearchParams(params)}`,
                body,
                {
                    headers,
                    user: idPu ? user : undefined,
                    ttl: Number.parseInt(appConfig.cache.ttl.products),
                    tags: [CACHE_TAGS.PRODUCTS],
                },
            );
        } catch {
            throw new Error('Could not fetch sapic products');
        }
    }

    private createSapicPayload(virtualCart: SimulatorCart, idPu?: string): SapicContextualisedProductPayload {
        const payload: SapicContextualisedProductPayload = {
            referenceExterneParcoursCible: '',
            panier: {
                parcours: virtualCart.cart.quotes?.map((quote, quoteIndex) => {
                    const mappedQuote: JourneyType = {
                        referenceExterne: `idParcours-${quoteIndex}`,
                        typeParcours: quote.type,
                        statut: quote.isCurrent ? 'EN_COURS' : 'TERMINE',
                        elements: quote.products?.map((product, index) => ({
                            referenceExterne: `idElement-${quoteIndex}-${index}`,
                            type: 'ELEMENT_PANIER',
                            composantCommercial: {
                                identifiant: product.gencode,
                                typeCatalogue: 'BYTEL',
                            },
                        })),
                    };
                    if (quote.contractId) {
                        mappedQuote.contrat = {
                            identifiant: quote.contractId,
                        };
                    }
                    return mappedQuote;
                }),
            },
        };

        if (idPu) {
            payload.panier.acheteur = {
                type: 'PERSONNE',
                noPersonne: idPu,
            };
        } else {
            payload.panier.acheteur = {
                type: 'NON_CONNU_GP',
            };
        }

        payload.referenceExterneParcoursCible =
            payload.panier.parcours.find((p) => p.statut === 'EN_COURS')?.referenceExterne || '';
        return payload;
    }

    private generateSearchParam(options: SapicGetOptionsType, idPu?: string): SapicProductPayload {
        const paramsBase = this.generateSearchParamBase(options, idPu);

        let filter = '';

        if (options.filterByTypes) {
            filter = this.createFilter(options.filterByTypes);
        }

        if (options.play) {
            filter = filter ? `${filter} and play = "${options.play}"` : `play = "${options.play}"`;
        }

        if (options.filterByGencodes) {
            const gencodesFilter = options.filterByGencodes.map((gencode) => `"${gencode}"`).join(',');
            filter = `gencode in (${gencodesFilter})`;
        }

        if (options.excludeCategoryType) {
            filter = filter
                ? `${filter} and typeCategorieFai != "${options.excludeCategoryType}"`
                : `typeCategorieFai != "${options.excludeCategoryType}"`;
        }

        const params: SapicProductPayload = {
            ...paramsBase,
            ...(options.gencodes && { gencodes: options.gencodes.join(',') }),
        };

        if (filter) {
            params.filtre = filter;
        }

        if (options.inStock) {
            params.stock = 'enStock';
        }

        return params;
    }

    private createFilter(categories: SapicCategoryId[] = []): string {
        let typeFilter = `type = "${categories[0]}"`;

        if (categories.length > 0) {
            const to = categories.map((category) => `"${category}"`).join(',');
            typeFilter = `type in (${to})`;
        }

        return typeFilter;
    }

    private generateSearchParamBase(options: SapicGetOptionsType, idPu?: string): SapicProductPayload {
        const callbackOptions: SapicGetOptionsType = { ...this.defaultGetProductsOptions, ...options };

        return {
            modePourFinancement: callbackOptions.fundingMode,
            prixReferencePourFiltre: callbackOptions.priceFilter,
            tri: callbackOptions.sort,
            detail: callbackOptions?.withDetails ? '1' : '0',
            limite: callbackOptions.limit?.toString(),
            decalage: callbackOptions.offset?.toString(),
            prixReferencePourTri: callbackOptions.priceSort,
            ...(callbackOptions.category && { categorie: callbackOptions.category }),
            ...(callbackOptions.categories && { categorie: callbackOptions.categories.join(',') }),
            ...(callbackOptions.gencodes && { gencodes: callbackOptions.gencodes }),
            // SAPIC Automatically search the right clientCategorie if idPu is provided
            ...(idPu ? { idPu } : { clientCategorie: SapicCustomerCategory.GP }),
            ...options.searchParams,
        } as SapicProductPayload;
    }
}

export const sapicRepository = new SapicRepository(appConfig.b2r.url);
